'use client';

import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';

import { memo } from 'react';
import Link from 'next/link';
import classNames from 'classnames';

import SwiperButtonPrev from 'app/components/common/SwiperButtonPrev';
import SwiperButtonNext from 'app/components/common/SwiperButtonNext';
import { IBranchPreview } from 'app/interfaces/branches';
import WithSwiper from 'app/components/common/WithSwiper';
import { IBranchContactsSliderTheme } from 'app/interfaces/ui';

import styles from './Slider.module.scss';
import ImageBg from '../ImageBg';

function Slider({
  branches,
  onMouseEnter,
  onMouseLeave,
  className,
  theme
}: {
  branches: IBranchPreview[];
  onMouseEnter(id: string | number): void;
  onMouseLeave(id: number | string): void;
  className: string;
  theme: IBranchContactsSliderTheme;
}) {
  const { itemTheme, buttonTheme } = theme;
  const { bgColor, bgColorHover } = itemTheme;

  return (
    <Swiper
      slidesPerView={1.1}
      spaceBetween={-16}
      breakpoints={{
        768: {
          slidesPerView: 1.6,
          spaceBetween: -28
        },
        1280: {
          slidesPerView: 3,
          spaceBetween: -24
        }
      }}
      className={classNames(styles.slider)}
    >
      {branches.map((item, index) => {
        const specialists =
          item.specialists
            ?.filter(item => item.image_preview?.[0]?.url)
            ?.slice(0, 4) || [];
        return (
          <SwiperSlide
            key={index}
            className={classNames(styles.slide, 'px-3 md:px-6')}
          >
            <Link
              href={`/branches/${item.slug}`}
              onMouseEnter={() => onMouseEnter(item.id)}
              onMouseLeave={() => onMouseLeave(item.id)}
              className={classNames(
                'grid grid-cols-2 self-stretch rounded w-full p-3 md:p-5 gap-4 justify-between cursor-pointer',
                className,
                bgColor,
                bgColorHover
              )}
            >
              <div className="text-p1 text-start w-full">{item.name}</div>
              {!!specialists.length && (
                <div className="flex justify-end items-end w-full">
                  <div className="flex justify-self-start">
                    {specialists?.map((specialist, index) => {
                      return (
                        <div
                          key={index}
                          className={classNames(
                            'relative w-9 h-13 rounded overflow-hidden',
                            {
                              '-ml-2': index > 0
                            }
                          )}
                        >
                          <ImageBg
                            src={specialist.image_preview[0].url}
                            alt={specialist.name}
                            fill
                            placeholderDataURL={
                              specialist.image_preview[0].placeholder_data_url
                            }
                            className="w-full h-full object-cover"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="text-h1 text-balance text-start self-end">
                {item.address}
              </div>
              <div className="flex justify-end w-full self-end">
                <div>
                  <div className="text-h1 text-start">Время</div>
                  <div className="text-h1 text-start">{item.working_hours}</div>
                </div>
              </div>
            </Link>
          </SwiperSlide>
        );
      })}
      <WithSwiper>
        {({ isLocked }) =>
          !isLocked && (
            <div className="hidden absolute top-0 right-6 z-10 xl:flex gap-1 justify-end">
              <SwiperButtonPrev theme={buttonTheme} />
              <SwiperButtonNext theme={buttonTheme} />
            </div>
          )
        }
      </WithSwiper>
    </Swiper>
  );
}

export default memo(Slider);
